import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ProductFilters from './ProductFilters';
import ProductListTable from './ProductListTable';
import Pagination from './Pagination';
import ProductDetailView, { mapFormDataToApiPayload } from './ProductDetailView';
import { classifyError } from '../../utils/errorTaxonomy';
import {
  getProductDetail,
  listProducts,
  updateProduct,
  getCurrentUserRole,
  listCategories,
  listVendors,
  connectShopifyVendor,
  syncShopifyVendor,
  bulkMoveToDraft,
  bulkDeleteProducts,
  bulkPushToPendingApproval,
  exportProductsToCSV,
} from '../../services/shipturtleApi';
import { useProductsChildRoute } from './productsNavigation';
import './ManageProducts.css';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';

// Memoized component that only re-renders when products or related props change
const ProductListSection = React.memo(({ 
  products, 
  loading, 
  error,
  onRetry,
  paginatedProducts,
  onProductAction,
  isReadOnlyAuditor,
  userRole,
  userId,
  productListRef,
  startIndex,
  endIndex,
  filteredProducts,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  selectedProducts,
  onProductSelect,
  onSelectAll
}) => {
  console.log('ProductListSection render:', {
    selectedProducts,
    onProductSelect: typeof onProductSelect,
    onSelectAll: typeof onSelectAll,
    paginatedProductsCount: paginatedProducts?.length
  });
  
  return (
    <>
      <div className="resultsSummary">
        <p>
          Showing {startIndex + 1}-{Math.min(endIndex, totalCount || (filteredProducts ? filteredProducts.length : 0))} of {totalCount || (filteredProducts ? filteredProducts.length : 0)} unlisted products
        </p>
      </div>
      
      {/* Scrollable product list container */}
      <div 
        ref={productListRef}
        style={{ 
          position: 'relative', 
          minHeight: '600px',
          maxHeight: 'calc(100vh - 280px)',
          overflowY: 'auto',
          overflowX: 'auto'
        }}
      >
        {loading && products.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <div className="spinner"></div>
          </div>
        )}
        
        <ProductListTable 
          products={paginatedProducts}
          onProductAction={onProductAction}
          isReadOnly={isReadOnlyAuditor}
          userRole={userRole}
          isLoading={loading}
          // Only when there is nothing to show; with rows on screen the table
          // keeps them and the notice above carries the message.
          error={products.length === 0 ? error : null}
          onRetry={onRetry}
          userId={userId}
          selectedProducts={selectedProducts}
          onProductSelect={(productId, isSelected) => {
            onProductSelect(productId, isSelected);
          }}
          onSelectAll={(isSelected) => {
            onSelectAll(isSelected);
          }}
        />
      </div>

      {/* Pagination Controls */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  const keysToCompare = [
    'loading', 
    'currentPage', 
    'totalPages', 
    'totalCount',
    'startIndex',
    'endIndex',
    'isReadOnlyAuditor',
    'userRole',
    'userId'
  ];
  
  for (const key of keysToCompare) {
    if (prevProps[key] !== nextProps[key]) {
      console.log(`ProductListSection re-render triggered by ${key} change`);
      return false;
    }
  }
  
  // Deep comparison for arrays
  if (JSON.stringify(prevProps.paginatedProducts) !== JSON.stringify(nextProps.paginatedProducts)) {
    console.log('ProductListSection re-render triggered by paginatedProducts change');
    return false;
  }
  
  if (JSON.stringify(prevProps.selectedProducts) !== JSON.stringify(nextProps.selectedProducts)) {
    console.log('ProductListSection re-render triggered by selectedProducts change');
    return false;
  }
  
  // If we get here, props are equal - prevent re-render
  return true;
});

function UnlistedProducts() {
  // The open product lives in the URL: /products/unlisted/:productId
  const {
    childId: routeProductId,
    openChild: openProduct,
    closeChild: closeProduct,
    isCurrentChild,
  } = useProductsChildRoute('unlisted');

  // Get user info from Redux store
  const userTypeRaw = useSelector((state) => state.auth?.user?.user_type || 'vendor');
  const userId = useSelector((state) => state.auth?.user?.id);

  // Product state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isReadOnlyAuditor, setIsReadOnlyAuditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [shopifyStatus, setShopifyStatus] = useState(null);
  
  // Shopify sync field filters state
  const [shopifyFieldFilters, setShopifyFieldFilters] = useState({
    shopify_title: true,
    shopify_description: true,
    shopify_vendor: true,
    price: true,
    compare_price: true,
    shopify_category_name: true,
  });
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Ref for the scrollable product list container
  const productListRef = useRef(null);
  
  // Filter and search state - Default to unlisted status
  const [filters, setFilters] = useState({
    globalSearch: '',
    status: 'unlisted',  // Default to unlisted
    category: 'all',
    vendor: 'all',
    channel: 'all',
    inventory: { min: '', max: '' },
    dateRange: 'all',
    productStatus: 'all'
  });
  
  // Debounced filters for API calls (to prevent API call on every keystroke)
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const debounceTimerRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // fallback client-side page size
  const [serverPagination, setServerPagination] = useState(null); // set when API returns pagination metadata
  
  // Detail view state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailViewMode, setDetailViewMode] = useState(null);
  useTaraTerminalFocus('open_product', selectedProduct && {
    product_id: selectedProduct.id,
    title: selectedProduct.title,
    mode: detailViewMode,
  });
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [saveReason, setSaveReason] = useState('');
  
  // RBAC: Determine if user is Vendor
  const isVendor = userRole === 'vendor';
  
  // Function to load/reload products - Only unlisted products
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching unlisted products from API with filters:', debouncedFilters);
      
      // Build API params from DEBOUNCED filters
      const apiParams = {
        // Server-side pagination now uses limit & offset only
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        status: 'unlisted'  // Always filter for unlisted products
      };
      
      // Map frontend filters to backend API parameters.
      // No terminal_status filter here: this screen's base query already locks to
      // terminal_status='unlisted' (below), and the "Product Status" filter that would
      // set this is hidden on this screen (hideProductStatusFilter) for that reason.
      if (debouncedFilters.category && debouncedFilters.category !== 'all') {
        apiParams.category_id = debouncedFilters.category;
      }
      if (debouncedFilters.vendor && debouncedFilters.vendor !== 'all') {
        apiParams.vendor_id = debouncedFilters.vendor;
      }
      if (debouncedFilters.globalSearch) {
        apiParams.search = debouncedFilters.globalSearch;
      }
      if (debouncedFilters.inventory?.min) {
        apiParams.inventory_min = debouncedFilters.inventory.min;
      }
      if (debouncedFilters.inventory?.max) {
        apiParams.inventory_max = debouncedFilters.inventory.max;
      }
      
      // Handle date range filter
      if (debouncedFilters.dateRange && debouncedFilters.dateRange !== 'all') {
        const now = new Date();
        let dateFrom = null;
        
        switch (debouncedFilters.dateRange) {
          case 'today':
            dateFrom = new Date(now.setHours(0, 0, 0, 0));
            break;
          case 'week':
            dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'quarter':
            dateFrom = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'year':
            dateFrom = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            break;
        }
        
        if (dateFrom) {
          apiParams.date_from = dateFrom.toISOString();
        }
      }
      
      // Fetch products from API with filters
      const response = await listProducts(apiParams);
      
      console.log('API Response:', response);
      
      if (response && response.products) {
        console.log('Unlisted products loaded:', response.products.length);
        // terminal_status already comes straight from the API via the spread below —
        // no local alias needed; every product here is 'unlisted' by definition.
        // approval_status drives the "Status" column, same derivation as every other
        // product list screen (ManageProducts.js, AdminApprovalQueue.js).
        const unlistedProducts = response.products
          .filter(product => product.terminal_status === 'unlisted')
          .map(product => ({
            ...product,
            approval_status: product.state === 'pending'
              ? 'pending_approval'
              : (product.terminal_status || product.state || 'draft'),
          }));
        setProducts(unlistedProducts);
        // Use server-side pagination metadata when provided
        if (response.pagination) {
          setServerPagination(response.pagination);
        } else {
          setServerPagination(null);
        }
      } else {
        console.error('Invalid response format:', response);
        setError(classifyError(new Error('Invalid response format')));
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      // Classified, like every other list: the raw string embedded the
      // browser's own "Failed to fetch", which names no cause a user can act on.
      setError(err && err.classified ? err.classified : classifyError(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, currentPage, itemsPerPage]); // Add dependencies to prevent infinite loop
  
  // Fetch user role from backend on mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        console.log('User role from backend:', roleData);
        setUserRole(roleData.role);
        setIsReadOnlyAuditor(userTypeRaw === 'read_only_auditor');
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('vendor');
        setIsReadOnlyAuditor(false);
      }
    };
    fetchUserRole();
  }, [userTypeRaw]);
  
  // Fetch categories and vendors for filters
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const categoriesResponse = await listCategories();
        if (categoriesResponse && categoriesResponse.categories) {
          setCategories(categoriesResponse.categories);
        }
        
        if (userRole === 'admin') {
          const vendorsResponse = await listVendors();
          if (vendorsResponse && vendorsResponse.vendors) {
            setVendors(vendorsResponse.vendors);
          }
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    
    if (userRole) {
      fetchFilterOptions();
    }
  }, [userRole]);
  
  // Debounce filter changes for text inputs (wait 500ms after user stops typing)
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer for debounced update
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500); // 500ms delay
    
    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters]);
  
  // Fetch products based on user role and DEBOUNCED filters
  useEffect(() => {
    if (userRole) {
      loadProducts();
    }
  }, [userRole, debouncedFilters, currentPage]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    console.log('Filter change:', newFilters);
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
    setSelectedProducts([]); // Clear selection on filter change
  }, []);

  // Handle page changes
  const handlePageChange = useCallback((newPage) => {
    console.log('Page change to:', newPage);
    setCurrentPage(newPage);
    setSelectedProducts([]); // Clear selection on page change
  }, []);

  // Re-fetches the currently-open detail modal's product after a status action, so it
  // reflects the new terminal_status/state instead of staying stale until re-opened.
  const refreshSelectedProductIfOpen = useCallback(async (productId) => {
    setSelectedProduct(prev => {
      if (!prev || prev.id !== productId) return prev;
      getProductDetail(productId).then(response => {
        if (response?.product) {
          setSelectedProduct({ ...response.product, missing_fields: response.missing_fields || [] });
        }
      }).catch(() => {});
      return prev;
    });
  }, []);

  // Handle product actions
  const handleProductAction = useCallback(async (action, productId, additionalData = {}) => {
    console.log('Product action:', action, 'Product ID:', productId, 'Additional data:', additionalData);

    switch (action) {
      case 'edit':
      case 'view':
        openProduct(productId);
        break;
      case 'duplicate':
        alert('Duplicate functionality not implemented yet');
        break;
      case 'delete':
        try {
          setLoading(true);
          const { deleteProduct } = await import('../../services/shipturtleApi');
          await deleteProduct(productId, additionalData.reason);
          alert('Product deleted successfully');
          await loadProducts();
        } catch (err) {
          console.error('Error deleting product:', err);
          alert(`Failed to delete product: ${err.message || 'Please try again.'}`);
        } finally {
          setLoading(false);
        }
        break;
      case 'draft':
        try {
          setLoading(true);
          const { moveToDraft } = await import('../../services/shipturtleApi');
          await moveToDraft(productId, additionalData.reason);
          alert('Product moved to draft successfully');
          await loadProducts();
          await refreshSelectedProductIfOpen(productId);
        } catch (err) {
          console.error('Error moving product to draft:', err);
          alert(`Failed to move product to draft: ${err.message || 'Please try again.'}`);
        } finally {
          setLoading(false);
        }
        break;

      case 'active':
        try {
          setLoading(true);
          const { moveToActive } = await import('../../services/shipturtleApi');
          const response = await moveToActive(productId, additionalData.reason);
          if (response?.errors?.length) {
            alert(`Move to active failed: ${response.errors[0]}`);
          } else {
            alert('Move to active submitted — awaiting admin approval');
          }
          await loadProducts();
          await refreshSelectedProductIfOpen(productId);
        } catch (err) {
          console.error('Error moving product to active:', err);
          alert(`Failed to move product to active: ${err.message || 'Please try again.'}`);
        } finally {
          setLoading(false);
        }
        break;
      default:
        console.log('Unknown action:', action);
    }
  }, [loadProducts, openProduct, refreshSelectedProductIfOpen]);

  // Loads and shows the product named in the URL. A product that can't be
  // loaded sends the user back to the list rather than leaving a dead URL.
  const showProduct = useCallback(async (productId) => {
    try {
      setLoading(true);
      const response = await getProductDetail(productId);
      if (!isCurrentChild(productId)) return; // user moved on while it loaded
      if (response && response.product) {
        setSelectedProduct({
          ...response.product,
          missing_fields: response.missing_fields || []
        });
        setDetailViewMode('edit');
      } else {
        setError('Failed to load product details');
        closeProduct();
      }
    } catch (err) {
      if (!isCurrentChild(productId)) return;
      console.error('Error fetching product details:', err);
      setError('Failed to load product details. Please try again.');
      closeProduct();
    } finally {
      setLoading(false);
    }
  }, [isCurrentChild, closeProduct]);

  // The detail view follows the URL: entering /unlisted/:id opens that product
  // (row click, deep link, Forward), leaving it (Back, ×, tab switch) closes it.
  useEffect(() => {
    if (!routeProductId) {
      setSelectedProduct(null);
      setDetailViewMode(null);
      return;
    }
    showProduct(routeProductId);
  }, [routeProductId, showProduct]);

  // Handle saving product changes
  const handleSaveProduct = useCallback(async (updatedProductData, reason) => {
    // If reason is provided, save directly (called from ProductDetailView's internal modal)
    if (reason) {
      try {
        setLoading(true);
        
        const apiPayload = mapFormDataToApiPayload(updatedProductData, reason);        
        const response = await updateProduct(updatedProductData.id, apiPayload);
        
        if (response && response.product) {
          const successMessage = updatedProductData._pushToPendingApproval 
            ? 'Product successfully pushed to pending approval!'
            : 'Product saved successfully!';
          alert(successMessage);
          
          await loadProducts();
          
          closeProduct();
        } else {
          throw new Error('Failed to update product');
        }
      } catch (error) {
        console.error('Error saving product:', error);
        const errorMessage = updatedProductData._pushToPendingApproval
          ? `Error pushing to pending approval: ${error.message || 'Please try again.'}`
          : `Error saving product: ${error.message || 'Please try again.'}`;
        alert(errorMessage);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Otherwise, show modal for reason (legacy flow, if still needed)
    setPendingSaveData(updatedProductData);
    
    // If this is a push to pending approval action, set a default reason message and close detail view
    if (updatedProductData._pushToPendingApproval) {
      setSaveReason('Product pushed to pending approval with updates');
      // Close the ProductDetailView first
      setSelectedProduct(null);
      setDetailViewMode(null);
    }
    
    setShowReasonModal(true);
  }, [loadProducts, closeProduct]);

  // Confirm save with reason
  const handleConfirmSave = async () => {
    if (!saveReason || saveReason.trim() === '') {
      alert('A reason is required to save changes');
      return;
    }
    
    const isPushToPendingApproval = pendingSaveData?._pushToPendingApproval;
    
    try {
      setLoading(true);
      setShowReasonModal(false);
      
      const apiPayload = mapFormDataToApiPayload(pendingSaveData, saveReason.trim());
      const response = await updateProduct(pendingSaveData.id, apiPayload);
      
      if (response && response.product) {
        const successMessage = isPushToPendingApproval 
          ? 'Product successfully pushed to pending approval!'
          : 'Product saved successfully!';
        alert(successMessage);
        
        await loadProducts();
        
        closeProduct();
        setPendingSaveData(null);
        setSaveReason('');
      } else {
        throw new Error('Failed to update product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      const errorMessage = isPushToPendingApproval
        ? `Error pushing to pending approval: ${error.message || 'Please try again.'}`
        : `Error saving product: ${error.message || 'Please try again.'}`;
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel save
  const handleCancelSave = useCallback(() => {
    setShowReasonModal(false);
    setPendingSaveData(null);
    setSaveReason('');
  }, []);
  
  // Handle closing detail view (× / Cancel): back up to the list. Scoped to the
  // product it was opened for, so the detail view's delayed close after a save
  // can't close a different product opened in the meantime.
  const handleCloseDetailView = useCallback((productId) => closeProduct(productId), [closeProduct]);

  // Handle product selection
  const handleProductSelect = useCallback((productId, isSelected) => {
    console.log('Product select:', productId, isSelected);
    setSelectedProducts(prev => {
      const s = new Set(prev || []);
      if (isSelected) {
        s.add(productId);
      } else {
        s.delete(productId);
      }
      return Array.from(s);
    });
  }, []);

  // Calculate paginated products
  const paginatedProducts = useMemo(() => {
    if (serverPagination) {
      // Backend handles pagination, return all products
      return products;
    } else {
      // Client-side pagination fallback
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return products.slice(startIndex, endIndex);
    }
  }, [products, currentPage, itemsPerPage, serverPagination]);

  // Handle select all
  const handleSelectAll = useCallback((isSelected) => {
    console.log('Select all:', isSelected);
    const pageIds = (paginatedProducts || []).map(p => p.id);
    setSelectedProducts(prev => {
      const s = new Set(prev || []);
      if (isSelected) {
        pageIds.forEach(id => s.add(id));
      } else {
        // remove only ids from the current page
        pageIds.forEach(id => s.delete(id));
      }
      return Array.from(s);
    });
  }, [paginatedProducts]);

  // Bulk action handlers
  const handleExportData = useCallback(async () => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product to export');
      return;
    }

    try {
      setLoading(true);
      
      const result = await exportProductsToCSV(selectedProducts);
      
      if (result.csv_data) {
        // Create a blob from the CSV data
        const blob = new Blob([result.csv_data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `unlisted_products_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL
        URL.revokeObjectURL(url);
        
        alert(`Successfully exported ${result.product_count} product(s) with ${result.variant_count} variant(s)`);
      } else {
        alert('No data to export');
      }
      
    } catch (error) {
      console.error('Error exporting products:', error);
      alert(`Export failed: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  }, [selectedProducts]);

  const handleBulkAction = useCallback(async (action) => {
    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }

    let confirmMessage = '';
    if (action === 'draft') {
      confirmMessage = `Move ${selectedProducts.length} product(s) to draft?`;
    } else if (action === 'delete') {
      confirmMessage = `Delete ${selectedProducts.length} product(s)? This action will soft-delete the products.`;
    } else if (action === 'pending_approval') {
      confirmMessage = `Push ${selectedProducts.length} product(s) to pending approval?`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      
      let result;
      if (action === 'draft') {
        result = await bulkMoveToDraft(selectedProducts);
        alert(`Successfully moved ${result.moved_count} product(s) to draft`);
      } else if (action === 'pending_approval') {
        result = await bulkPushToPendingApproval(selectedProducts);
        alert(`Attempted to push ${selectedProducts.length} product(s) to pending approval. Updated: ${result.updated_count || 0}`);
      } else if (action === 'delete') {
        result = await bulkDeleteProducts(selectedProducts);
        alert(`Successfully deleted ${result.deleted_count} product(s)`);
      }
      
      if (result.errors && result.errors.length > 0) {
        console.warn('Bulk action had errors:', result.errors);
        alert(`Some products had errors:\n${result.errors.slice(0, 3).join('\n')}`);
      }
      
      // Reload products and clear selection
      await loadProducts();
      setSelectedProducts([]);
      
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert(`Error: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  }, [selectedProducts, loadProducts]);

  // Calculate pagination info
  const totalCount = serverPagination?.total || products.length;
  const totalPages = serverPagination?.pages || Math.ceil(products.length / itemsPerPage);
  const startIndex = serverPagination
    ? (serverPagination.page - 1) * (serverPagination.page_size || itemsPerPage)
    : (currentPage - 1) * itemsPerPage;
  const endIndex = serverPagination
    ? startIndex + paginatedProducts.length
    : startIndex + itemsPerPage;

  return (
    <div className="manageProductsContainer">
      <h2>Unlisted Products</h2>
      
      {/* Filters */}
      <ProductFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        categories={categories}
        vendors={vendors}
        userRole={userRole}
  hideStatusFilter={true}  // Hide status filter since we only show unlisted
  hideProductStatusFilter={true} // terminal_status is already locked to 'unlisted' here — every other option would return zero results
  hideStatusFromActive={true} // Don't show 'unlisted' as an active filter in header
  inventoryFullRow={true} // Put inventory inputs on their own row to avoid UI overlap
      dateRangeAtStatusPosition={true}
      />

      {/* Bulk Actions Bar */}
      {selectedProducts.length > 0 && !isReadOnlyAuditor && (
        <div className="bulkActionsBar">
          <div className="bulkActionsInfo">
            <span>{selectedProducts.length} product(s) selected</span>
          </div>
          <div className="bulkActionsButtons">
            <button onClick={handleExportData} className="bulkActionButton success">
              Export Data
            </button>
            <button onClick={() => handleBulkAction('pending_approval')} className="bulkActionButton primary">
              Push to Pending Approval
            </button>
            <button onClick={() => handleBulkAction('draft')} className="bulkActionButton secondary">
              Move to Draft
            </button>
            <button onClick={() => handleBulkAction('delete')} className="bulkActionButton danger">
              Delete
            </button>
          </div>
        </div>
      )}

      {/* A failed load is reported by the table itself, below, so that "no
          products" and "we could not read them" cannot both appear at once. */}

      {/* Loading spinner - only show if no products loaded yet */}
      {loading && products.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <div className="spinner"></div>
        </div>
      )}

      {/* Products list */}
      {!loading || products.length > 0 ? (
        <ProductListSection
          products={products}
          loading={loading}
          error={error}
          onRetry={loadProducts}
          paginatedProducts={paginatedProducts}
          onProductAction={handleProductAction}
          isReadOnlyAuditor={isReadOnlyAuditor}
          userRole={userRole}
          userId={userId}
          productListRef={productListRef}
          startIndex={startIndex}
          endIndex={endIndex}
          filteredProducts={products}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          selectedProducts={selectedProducts}
          onProductSelect={handleProductSelect}
          onSelectAll={handleSelectAll}
        />
      ) : null}

      {/* Product Detail View Modal */}
      {selectedProduct && (
        <ProductDetailView
          key={selectedProduct.id}
          product={selectedProduct}
          mode={detailViewMode}
          onClose={() => handleCloseDetailView(selectedProduct.id)}
          onSave={handleSaveProduct}
          userRole={userRole}
          isReadOnly={isReadOnlyAuditor}
        />
      )}

      {/* Reason Modal for Save Changes */}
      {showReasonModal && (
        <div className="modalOverlay" onClick={handleCancelSave}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <div className="modalHeader">
              <h2>
                {pendingSaveData?._pushToPendingApproval 
                  ? 'Push to Pending Approval' 
                  : 'Provide Reason for Changes'}
              </h2>
              <button className="modalCloseButton" onClick={handleCancelSave}>×</button>
            </div>
            <div className="modalBody">
              <p className="modalDescription">
                {pendingSaveData?._pushToPendingApproval 
                  ? 'This will save your changes and submit the product for admin approval. Please provide a reason for this submission.'
                  : 'Please provide a reason for these changes. This will be recorded in the audit history.'}
              </p>
              <label htmlFor="saveReason" className="modalLabel">
                Reason for {pendingSaveData?._pushToPendingApproval ? 'submission' : 'changes'} <span className="required">*</span>
              </label>
              <textarea
                id="saveReason"
                value={saveReason}
                onChange={(e) => setSaveReason(e.target.value)}
                placeholder={pendingSaveData?._pushToPendingApproval 
                  ? "E.g., Product is ready for review, All required information has been added, etc."
                  : "E.g., Updated product description based on vendor feedback, Corrected pricing error, etc."}
                rows="4"
                className="modalTextarea"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleCancelSave();
                  } else if (e.key === 'Enter' && e.ctrlKey && saveReason.trim()) {
                    handleConfirmSave();
                  }
                }}
              />
              <div className="modalCharCount">
                {saveReason.length} characters • Press <kbd>Ctrl+Enter</kbd> to save, <kbd>Esc</kbd> to cancel
              </div>
            </div>
            <div className="modalFooter">
              <button
                className="modalCancelButton"
                onClick={handleCancelSave}
                type="button"
              >
                Cancel
              </button>
              <button
                className="modalSaveButton"
                onClick={handleConfirmSave}
                type="button"
                disabled={!saveReason.trim()}
              >
                {pendingSaveData?._pushToPendingApproval 
                  ? 'Submit for Approval' 
                  : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UnlistedProducts;
